import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ServiceRequestFormData, OrganizationData } from '../../types';
import { canAccessFeature } from '../../utils/tierPermissions';
import { useFormVerification, VerificationChannel } from '../../hooks/useFormVerification';
import VerificationCodePrompt from '../Agent/VerificationCodePrompt';
import ServiceMachineCard, { ServiceMachineData } from '../Agent/ServiceMachineCard';
import ConsentBlock from './ConsentBlock';
import './StepStyles.scss';
import '../Agent/AgentChat.scss';

interface Step1WelcomeProps {
  formData: ServiceRequestFormData;
  updateFormData: (data: Partial<ServiceRequestFormData>) => void;
  registerValidation: (validator: () => boolean) => void;
  organizationData: OrganizationData;
}

function Step1Welcome({
    formData,
    updateFormData,
    registerValidation,
    organizationData
}: Step1WelcomeProps) {
  const [errors, setErrors] = useState<{ consent?: string; mobilePhone?: string }>({});

  // --- Returning-customer verify-to-autofill (gated to AI-agent tiers) ---
  const verifyEnabled = canAccessFeature(organizationData.organization.tier, 'aiAgent');
  const verification = useFormVerification(String(organizationData.organization.id));
  const [verifyChannel, setVerifyChannel] = useState<VerificationChannel>('sms');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyDismissed, setVerifyDismissed] = useState(false);
  const prefilledRef = useRef(false);

  // Internal category -> the display_category that surfaces it (if any). Step 4 hides
  // internal categories that roll up under a display_category, so a verified-machine
  // prefill must also set selectedDisplayCategoryId — otherwise Step 4 can't resolve the
  // selection and its reset effect wipes the prefilled machine entirely.
  const displayCatByCategory = useMemo(() => {
    const map = new Map<number, number>();
    for (const m of organizationData.displayCategoryMappings) {
      if (!map.has(m.category_id)) map.set(m.category_id, m.display_category_id);
    }
    return map;
  }, [organizationData.displayCategoryMappings]);

  // Build the Step-4 machine selection fields from a machine on file, mirroring what a
  // manual category->make->machine pick would produce (incl. the display category).
  const machineSelectionFields = useCallback(
    (m: ServiceMachineData): Partial<ServiceRequestFormData> => {
      const categoryId = m.category_id ?? null;
      return {
        selectedCategoryId: categoryId,
        selectedDisplayCategoryId:
          categoryId != null ? displayCatByCategory.get(categoryId) ?? null : null,
        selectedMakeId: m.make_id,
        selectedMachineId: m.machine_id,
        selectedMachineName: m.machine_name,
      };
    },
    [displayCatByCategory],
  );

  const validateStep = () => {
    const newErrors: { consent?: string; mobilePhone?: string } = {};
    let isValid = true;

    if (!formData.consentGiven) {
      newErrors.consent = 'Please agree to the terms to continue.';
      isValid = false;
    }

    if (formData.smsConsent && (!formData.mobilePhone || !formData.mobilePhone.trim())) {
      newErrors.mobilePhone = 'Please enter your mobile phone number to receive SMS updates.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  // On a successful verification, prefill the form from the customer on file.
  useEffect(() => {
    if (verification.phase !== 'verified' || !verification.customer || prefilledRef.current) {
      return;
    }
    prefilledRef.current = true;
    const c = verification.customer;
    const update: Partial<ServiceRequestFormData> = {
      firstName: c.firstName ?? '',
      lastName: c.lastName ?? '',
      email: c.email ?? '',
      mobilePhone: c.mobilePhone ?? '',
      homePhone: c.homePhone ?? '',
      address: c.address ?? '',
      city: c.city ?? '',
      state: c.state ?? '',
      zipCode: c.zipCode ?? '',
      preferredContact: (c.preferredContact as ServiceRequestFormData['preferredContact']) ?? '',
      customerId: c.customerId ?? null,
      customerAddressId: c.customerAddressId ?? null,
    };
    // Reflect (never downgrade) existing SMS consent so the section matches their record.
    if (c.smsConsent) {
      update.smsConsent = true;
    }
    // Single machine on file → prefill the form's one-machine selection directly.
    if (verification.machines.length === 1) {
      Object.assign(update, machineSelectionFields(verification.machines[0]));
    }
    updateFormData(update);
  }, [verification.phase, verification.customer, verification.machines, updateFormData, machineSelectionFields]);

  const handleSelectMachine = (m: ServiceMachineData) => {
    updateFormData(machineSelectionFields(m));
  };

  const handleSendCode = () => {
    const destination = verifyChannel === 'sms' ? (formData.mobilePhone || '') : verifyEmail;
    verification.requestCode(verifyChannel, destination);
  };

  // "Skip — continue as guest": clear verify state and hide the section entirely.
  const handleSkipVerify = () => {
    verification.reset();
    setVerifyDismissed(true);
  };

  const termsLink = organizationData.organizationSettings?.terms_link;
  const primaryPhoneNumber = organizationData.organizationPhoneNumber?.phone_number;
  const orgName = organizationData.organization.name;
  const isVerifying = verification.phase === 'awaiting_code' || verification.phase === 'verifying';

  return (
    <div className="form-step">
      <h2>Welcome!</h2>
      <p className="step-intro">
        Please review the terms and conditions before proceeding...
      </p>

      {primaryPhoneNumber && (
        <div className="call-option-banner">
          <p>
            Or call us at <a href={`tel:${primaryPhoneNumber}`} className="phone-link">{primaryPhoneNumber}</a> to speak with our AI assistant.
          </p>
        </div>
      )}

      {verifyEnabled && !verifyDismissed && (
        <div className="returning-customer-verify">
          {verification.phase === 'verified' ? (
            <div className="rcv-success">
              <p className="rcv-success__headline">
                ✓ Welcome back{verification.customer?.firstName ? `, ${verification.customer.firstName}` : ''}!
              </p>
              <p className="rcv-success__sub">
                We've loaded your details below — review and edit anything that's changed.
              </p>
              {verification.machines.length > 1 ? (
                <>
                  <ServiceMachineCard
                    machines={verification.machines}
                    onSelect={handleSelectMachine}
                    selectedMachineId={formData.selectedMachineId ?? null}
                    hint={null}
                  />
                  {formData.selectedMachineId && (
                    <p className="rcv-picked">
                      Selected: <strong>{formData.selectedMachineName}</strong>
                    </p>
                  )}
                </>
              ) : verification.machines.length === 1 ? (
                <p className="rcv-picked">
                  Equipment on file:{' '}
                  <strong>
                    {verification.machines[0].make_name} {verification.machines[0].machine_name}
                    {verification.machines[0].pfw_serial_number
                      ? ` (S/N ${verification.machines[0].pfw_serial_number})`
                      : ''}
                  </strong>
                </p>
              ) : null}
            </div>
          ) : verification.phase === 'no_match' ? (
            <div className="rcv-nomatch">
              <p>We couldn't find an account for that contact — no problem. Just fill out the form below.</p>
              <button type="button" className="rcv-link-btn" onClick={verification.reset}>
                Try a different phone or email
              </button>
            </div>
          ) : isVerifying ? (
            <VerificationCodePrompt
              channel={verification.channel}
              maskedDestination={verification.maskedDestination}
              canResend={verification.canResend}
              error={verification.error}
              disabled={verification.phase === 'verifying'}
              onSubmit={verification.submitCode}
              onResend={verification.resend}
              onSkip={handleSkipVerify}
            />
          ) : (
            <div className="rcv-start">
              <div className="rcv-start__header">
                <strong>Returning customer?</strong>
                <span>Verify your phone or email to autofill your info and equipment on file.</span>
              </div>
              <div className="rcv-channel-toggle">
                <button
                  type="button"
                  className={verifyChannel === 'sms' ? 'active' : ''}
                  aria-pressed={verifyChannel === 'sms'}
                  onClick={() => setVerifyChannel('sms')}
                >
                  Text me a code
                </button>
                <button
                  type="button"
                  className={verifyChannel === 'email' ? 'active' : ''}
                  aria-pressed={verifyChannel === 'email'}
                  onClick={() => setVerifyChannel('email')}
                >
                  Email me a code
                </button>
              </div>
              {verifyChannel === 'sms' ? (
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  value={formData.mobilePhone || ''}
                  onChange={(e) => updateFormData({ mobilePhone: e.target.value })}
                  aria-label="Mobile phone for verification"
                />
              ) : (
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={verifyEmail}
                  onChange={(e) => setVerifyEmail(e.target.value)}
                  aria-label="Email for verification"
                />
              )}
              {verification.error && <span className="error-text">{verification.error}</span>}
              <button
                type="button"
                className="rcv-send-btn"
                disabled={verification.phase === 'sending'}
                onClick={handleSendCode}
              >
                {verification.phase === 'sending' ? 'Sending…' : 'Send code'}
              </button>
            </div>
          )}
        </div>
      )}

      <ConsentBlock formData={formData} updateFormData={updateFormData} termsLink={termsLink} orgName={orgName} errors={errors} />
    </div>
  );
}

export default Step1Welcome;
